<script setup>
import { computed, watch, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRoute } from 'vue-router';
import DropdownMenuApp from '@/components/utilApp/DropdownMenuApp.vue';
import DropdownSubmenuApp from '@/components/utilApp/DropdownSubmenuApp.vue';
import DropdownSubmenuItemApp from '@/components/utilApp/DropdownSubmenuItemApp.vue';
import DropdownMenuItemApp from '@/components/utilApp/DropdownMenuItemApp.vue';
import MenuItemApp from '@/components/utilApp/MenuItemApp.vue';
import MenuHeaderApp from '@/components/utilApp/MenuHeaderApp.vue';
import MenuTitleApp from '@/components/utilApp/MenuTitleApp.vue';
import { useMenuState } from '@/composables/useMenuState';
import { props as propsCaixa } from '@/modules/financeiro/financeiroCaixaMov/store';

const route = useRoute();
const { hideMenu, isMenuOpen, snapshotAndCloseAll, restoreFromSnapshot, closeAllItems } = useMenuState();

// Refs for event listener cleanup
const mainMenuRef = ref(null);
const navbarHeaderRef = ref(null);

// Event handlers for modern menu expand/collapse
const handleMouseEnter = () => {
  const $body = document.body;
  if ($body.dataset.menu === 'vertical-menu-modern') {
    const mainMenu = document.querySelector('.main-menu');
    const navbarHeader = document.querySelector('.navbar-header');
    mainMenu?.classList.add('expanded');
    navbarHeader?.classList.add('expanded');

    // Handle collapsed menu expansion — restore previously open items
    if ($body.classList.contains('menu-collapsed')) {
      restoreFromSnapshot();
    }
  }
};

const handleMouseLeave = () => {
  const $body = document.body;
  if ($body.classList.contains('menu-collapsed') && $body.dataset.menu === 'vertical-menu-modern') {
    setTimeout(() => {
      const mainMenu = document.querySelector('.main-menu');
      const navbarHeader = document.querySelector('.navbar-header');

      if (!mainMenu?.matches(':hover') && !navbarHeader?.matches(':hover')) {
        mainMenu?.classList.remove('expanded');
        navbarHeader?.classList.remove('expanded');

        // Snapshot open items and close all via centralized state
        if ($body.classList.contains('menu-collapsed')) {
          snapshotAndCloseAll();
        }
      }
    }, 1);
  }
};

// Setup hover events when component is mounted
onMounted(() => {
  // Use nextTick to ensure DOM is fully rendered
  setTimeout(() => {
    const mainMenu = document.querySelector('.main-menu');
    const navbarHeader = document.querySelector('.navbar-header');

    if (mainMenu) {
      mainMenuRef.value = mainMenu;
      mainMenu.addEventListener('mouseenter', handleMouseEnter);
      mainMenu.addEventListener('mouseleave', handleMouseLeave);
    }

    if (navbarHeader) {
      navbarHeaderRef.value = navbarHeader;
      navbarHeader.addEventListener('mouseenter', handleMouseEnter);
      navbarHeader.addEventListener('mouseleave', handleMouseLeave);
    }
  }, 100);
});

// Cleanup event listeners when component is unmounted
onBeforeUnmount(() => {
  if (mainMenuRef.value) {
    mainMenuRef.value.removeEventListener('mouseenter', handleMouseEnter);
    mainMenuRef.value.removeEventListener('mouseleave', handleMouseLeave);
  }

  if (navbarHeaderRef.value) {
    navbarHeaderRef.value.removeEventListener('mouseenter', handleMouseEnter);
    navbarHeaderRef.value.removeEventListener('mouseleave', handleMouseLeave);
  }
});

const getTitleCaixa = computed({
  get: () => (propsCaixa.value.isAberto ? 'Fechar caixa' : 'Abrir caixa'),
});

const getRouterFecharCaixa = computed({
  get: () => (propsCaixa.value.isAberto ? 'caixa-fechar' : 'caixa-abrir'),
});

// Close menu on mobile when navigating to a new route
watch(
  () => route.fullPath,
  () => {
    const isMobile = window.innerWidth < 1200;
    if (isMobile && isMenuOpen.value) {
      hideMenu();
      closeAllItems();
    }
  },
);
</script>
<template>
  <div class="main-menu menu-fixed menu-light menu-accordion menu-shadow" data-scroll-to-active="true" style="touch-action: none; -webkit-user-select: none; -webkit-user-drag: none">
    <MenuHeaderApp />
    <div class="main-menu-content">
      <div class="card-body px-0 pt-0 menu-scroll">
        <div class="align-content-start">
          <ul class="navigation navigation-main" id="main-menu-navigation" data-menu="menu-navigation">
            <MenuTitleApp :menu-header="'Início'" />
            <MenuItemApp :title="'Home'" :router-link="'home'" :fontawesome="'fa-solid fa-house'" />
            <MenuTitleApp :menu-header="'Estoque'" />
            <MenuItemApp :title="'Produto'" :router-link="'produto-listar'" :fontawesome="'fa-solid fa-box-archive'" />
            <MenuItemApp :title="'Contagem de estoque'" :router-link="'contagemEstoque-cadastrar'" :fontawesome="'fa-solid fa-cart-plus'" />
            <MenuItemApp :title="'Entrada'" :router-link="'entrada-listar'" :fontawesome="'fa-solid fa-cart-plus'" />
            <DropdownMenuApp :title="'Ver mais'" class="menu-dropdown" :fontawesome="'fa-solid fa-plus'">
              <DropdownSubmenuApp :title-submenu="'Produção'">
                <DropdownSubmenuItemApp :title="'Composição'" :router-link="'produtoComposicao-listar'" />
                <DropdownSubmenuItemApp :title="'Gerenciar ordem'" :router-link="'ordemProducao-listar'" />
              </DropdownSubmenuApp>
              <DropdownMenuItemApp :title="'Tabela de preço'" :router-link="'tabelaPreco-listar'" />
              <DropdownSubmenuApp :title-submenu="'Impostos'" item-id="estoque-impostos">
                <DropdownSubmenuItemApp :title="'Classe de imposto'" :router-link="'classeImposto-listar'" />
                <DropdownSubmenuItemApp :title="'CFOP'" :router-link="'cfop-listar'" />
                <DropdownSubmenuItemApp :title="'NCM'" :router-link="'ncm-listar'" />
                <DropdownSubmenuItemApp :title="'CEST'" :router-link="'cest-listar'" />
                <DropdownSubmenuItemApp :title="'Carga tributária'" :router-link="'cargaTributaria-listar'" />
                <DropdownSubmenuItemApp :title="'Tabela ICMS'" :router-link="'icmsUfs-listar'" />
              </DropdownSubmenuApp>
              <DropdownSubmenuApp :title-submenu="'Classificações'" item-id="estoque-classificacoes">
                <DropdownSubmenuItemApp :title="'Classif. mercadológica'" :router-link="'classificacaoMercadologica-listar'" />
                <DropdownSubmenuItemApp :title="'Classe'" :router-link="'classe-listar'" />
                <DropdownSubmenuItemApp :title="'Subclasse'" :router-link="'subclasse-listar'" />
                <DropdownSubmenuItemApp :title="'Grupo'" :router-link="'grupo-listar'" />
                <DropdownSubmenuItemApp :title="'Familia'" :router-link="'familia-listar'" />
                <DropdownSubmenuItemApp :title="'Marca'" :router-link="'marca-listar'" />
                <DropdownSubmenuItemApp :title="'Unidade venda'" :router-link="'unidade-listar'" />
              </DropdownSubmenuApp>
              <DropdownSubmenuApp :title-submenu="'Transporte'" item-id="estoque-transporte">
                <DropdownSubmenuItemApp :title="'Veículo'" :router-link="'veiculo-listar'" />
              </DropdownSubmenuApp>
              <DropdownMenuItemApp :title="'Transferências'" :router-link="'transferencia-listar'" />
              <DropdownSubmenuApp :title-submenu="'Grade'" item-id="estoque-grade">
                <DropdownSubmenuItemApp :title="'Tamanho'" :router-link="'tamanho-listar'" />
                <DropdownSubmenuItemApp :title="'Cor'" :router-link="'cor-listar'" />
              </DropdownSubmenuApp>
            </DropdownMenuApp>
            <MenuTitleApp :menu-header="'Comercial'" />
            <MenuItemApp :title="'Saída'" :router-link="'saida-listar'" :fontawesome="'fa-solid fa-cart-shopping'" />
            <MenuItemApp :title="'O.S.'" :router-link="'ordemServico-listar'" :fontawesome="'fa-solid fa-screwdriver-wrench'" />
            <MenuItemApp :title="'Consultar caixas'" :router-link="'caixa-consultarFechamentos'" :fontawesome="'fa-solid fa-file-circle-check'" />
            <DropdownMenuApp :title="'Ver mais'" class="menu-dropdown" :fontawesome="'fa-solid fa-plus'" item-id="comercial-ver-mais">
              <DropdownSubmenuApp :title-submenu="'Ordem de serviço'" item-id="comercial-os">
                <DropdownSubmenuItemApp :title="'O.S.'" :router-link="'ordemServico-listar'" />
                <DropdownSubmenuItemApp :title="'Tipo de O.S.'" :router-link="'osTipo-listar'" />
                <DropdownSubmenuItemApp :title="'Situação de O.S.'" :router-link="'osSituacao-listar'" />
                <DropdownSubmenuItemApp :title="'Campo personalizado'" :router-link="'dadosOsConfig-cadastrar'" />
              </DropdownSubmenuApp>
              <DropdownSubmenuApp :title-submenu="'Relatórios'" item-id="comercial-relatorios">
                <DropdownSubmenuItemApp :title="'Resumo vendas'" :router-link="'resumoVendas-welcome'" />
                <DropdownSubmenuItemApp :title="'Vendas de produtos'" :router-link="'relVendasProdutos-listar'" />
                <DropdownSubmenuItemApp :title="'Resumo por vendedor'" :router-link="'relComissaoResumoPorVendedor-listar'" />
                <DropdownSubmenuItemApp :title="'Por tipo de recebimento'" :router-link="'relVendasPorRecebimento-listar'" />
                <DropdownSubmenuItemApp :title="'Vendas por cliente'" :router-link="'relVendasPorCliente-listar'" />
              </DropdownSubmenuApp>
              <DropdownSubmenuApp :title-submenu="'Notas fiscais'" item-id="comercial-nf">
                <DropdownSubmenuItemApp :title="'Exportar XML'" :router-link="'exportXml'" />
              </DropdownSubmenuApp>
              <DropdownSubmenuApp :title-submenu="'Venda perdida'" item-id="comercial-venda-perdida">
                <DropdownSubmenuItemApp :title="'Gerenciar'" :router-link="'vendaPerdida-listar'" />
                <DropdownSubmenuItemApp :title="'Motivos'" :router-link="'motivoVendaPerdida-listar'" />
                <DropdownSubmenuItemApp :title="'Relatório'" :router-link="'vendaPerdida-Relatorio'" />
              </DropdownSubmenuApp>
            </DropdownMenuApp>
            <MenuTitleApp :menu-header="'Financeiro'" />
            <MenuItemApp :title="getTitleCaixa" :router-link="getRouterFecharCaixa" :fontawesome="'fa-solid fa-cash-register'" />
            <MenuItemApp :title="'Recebível'" :router-link="'financeiroConta-listar'" :fontawesome="'fa-solid fa-hand-holding-dollar'" />
            <MenuItemApp :title="'Conta a pagar'" :router-link="'contaPagar-listar'" :fontawesome="'fa-solid fa-handshake'" />
            <MenuItemApp :title="'Carnê rápido'" :router-link="'financeiroConta-carneRapido'" :fontawesome="'fa-solid fa-file-circle-exclamation'" />
            <MenuItemApp :title="'Créditos'" :router-link="'credito-listar'" :fontawesome="'fa-solid fa-circle-dollar-to-slot'" />
            <DropdownMenuApp :title="'Caixa PDV'" :fontawesome="'fa-solid fa-circle-plus'" item-id="financeiro-caixa-pdv">
              <DropdownMenuItemApp :title="'Entrada caixa'" :router-link="'caixa-entrada'" />
              <DropdownMenuItemApp :title="'Retirada caixa'" :router-link="'caixa-retirada'" />
              <DropdownMenuItemApp :title="'Consultar caixas'" :router-link="'caixa-consultarFechamentos'" />
              <DropdownMenuItemApp :title="'Cadastrar caixas'" :router-link="'caixa-listar'" />
            </DropdownMenuApp>
            <DropdownMenuApp :title="'Recebíveis'" :fontawesome="'fa-solid fa-circle-plus'" item-id="financeiro-recebiveis">
              <DropdownMenuItemApp :title="'Recebível'" :router-link="'financeiroConta-listar'" />
              <DropdownMenuItemApp :title="'Bandeira de cartão'" :router-link="'bandeiraCartao-listar'" />
              <DropdownMenuItemApp :title="'Adm. de cartão'" :router-link="'administradoraCartao-listar'" />
              <DropdownMenuItemApp :title="'Forma de pagamento'" :router-link="'formaPagamento-listar'" />
              <DropdownMenuItemApp :title="'Banco'" :router-link="'banco-listar'" />
              <DropdownMenuItemApp :title="'Conta bancária'" :router-link="'contaBancaria-listar'" />
              <DropdownMenuItemApp :title="'Contrato'" :router-link="'financeiroContaContrato-listar'" />
            </DropdownMenuApp>
            <DropdownMenuApp :title="'Contas a pagar'" :fontawesome="'fa-solid fa-handshake'" item-id="financeiro-contas-pagar">
              <DropdownMenuItemApp :title="'Conta a pagar'" :router-link="'contaPagar-listar'" />
              <DropdownMenuItemApp :title="'Centro de custo'" :router-link="'centroCusto-listar'" />
              <DropdownSubmenuApp :title-submenu="'Plano de contas'" item-id="financeiro-plano-contas">
                <DropdownSubmenuItemApp :title="'Nível 1'" :router-link="'planoConta1-listar'" />
                <DropdownSubmenuItemApp :title="'Nível 2'" :router-link="'planoConta2-listar'" />
                <DropdownSubmenuItemApp :title="'Nível 3'" :router-link="'planoConta3-listar'" />
              </DropdownSubmenuApp>
            </DropdownMenuApp>
            <MenuTitleApp :menu-header="'CRM'" />
            <MenuItemApp :title="'Pessoas'" :router-link="'pessoa-listar'" :fontawesome="'fa-solid fa-user'" />
            <DropdownMenuApp :title="'Ver mais'" class="menu-dropdown" :fontawesome="'fa-solid fa-plus'" item-id="crm-ver-mais">
              <DropdownMenuItemApp :title="'Cidades'" :router-link="'cidades-listar'" />
              <DropdownMenuItemApp :title="'Países'" :router-link="'pais-listar'" />
            </DropdownMenuApp>
            <MenuTitleApp :menu-header="'Configurações'" />
            <MenuItemApp :title="'Filial'" :fontawesome="'fa-solid fa-building'" :router-link="'filial-listar'" />
            <MenuItemApp :title="'Funcionário'" :fontawesome="'fa-solid fa-user-gear'" :router-link="'funcionario-listar'" />
            <DropdownMenuApp :title="'Ver mais'" class="menu-dropdown" :fontawesome="'fa-solid fa-plus'" item-id="configuracoes-ver-mais">
              <DropdownMenuItemApp :title="'Operação'" :router-link="'operacao-listar'" />
              <DropdownMenuItemApp :title="'Permissão'" :router-link="'permissaoGrupo-listar'" />
              <DropdownMenuItemApp :title="'WebServices'" :router-link="'nfeWebService-cadastrar'" />
              <DropdownMenuItemApp :title="'Partner'" :router-link="'partner-cadastrar'" />
              <DropdownMenuItemApp :title="'Feriados'" :router-link="'feriado-listar'" />
              <DropdownMenuItemApp :title="'DAV'" :router-link="'dav-report-listar'" />
            </DropdownMenuApp>
            <MenuTitleApp :menu-header="'Outros'" />
            <MenuItemApp :title="'Importar arquivo texto'" :router-link="'importarArquivoTexto-welcome'" :fontawesome="'fa-solid fa-file-import'" />
            <DropdownMenuApp :title="'Gourmet'" class="menu-dropdown" :fontawesome="'fa-solid fa-plus'" item-id="gourmet">
              <DropdownMenuItemApp :title="'Configurações'" :router-link="'configurar-tela-gourmet'" />
              <DropdownMenuItemApp :title="'Área de impressão'" :router-link="'areaImpressao-listar'" />
              <DropdownMenuItemApp :title="'Mesa'" :router-link="'mesa-listar'" />
              <DropdownMenuItemApp :title="'Setor'" :router-link="'setor-listar'" />
              <DropdownMenuItemApp :title="'Produto favorito'" :router-link="'favoritoProduto-cadastrar'" />
              <DropdownMenuItemApp :title="'Licença GourmetDroid'" :router-link="'licencaDroid'" />
              <DropdownSubmenuApp :title-submenu="'Adicionais'" item-id="gourmet-adicionais">
                <DropdownSubmenuItemApp :title="'Produto'" :router-link="'adicionalProduto-listar'" />
                <DropdownSubmenuItemApp :title="'Classificação mercadológica'" :router-link="'adicionalClassificacaoMercadologica-listar'" />
              </DropdownSubmenuApp>
              <DropdownMenuItemApp :title="'Área de entrega'" :router-link="'areaEntrega-listar'" :fontawesome="'fa-solid fa-truck-ramp-box'" />
              <DropdownMenuItemApp :title="'Kit'" :router-link="'kit-listar'" :fontawesome="'fa-solid fa-truck-ramp-box'" />
            </DropdownMenuApp>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.vertical-layout.vertical-menu-modern.menu-collapsed .main-menu #collapseOne {
  height: 0px;
  visibility: hidden;
}

.vertical-layout.vertical-menu-modern.menu-collapsed .main-menu.expanded #collapseOne {
  height: auto;
  visibility: visible;
}

.vertical-layout.vertical-menu-modern.menu-collapsed .main-menu #buttonRotate,
.vertical-layout.vertical-menu-modern.menu-collapsed .main-menu #menuHide {
  display: none;
}

.vertical-layout.vertical-menu-modern.menu-collapsed .main-menu.expanded #buttonRotate,
.vertical-layout.vertical-menu-modern.menu-collapsed .main-menu.expanded #menuHide {
  display: flex;
}

.nav-group-label::before {
  position: absolute;
  border-radius: inherit;
  background: currentcolor;
  block-size: 100%;
  content: "";
  inline-size: 100%;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.button {
  transform-origin: center;
  flex-shrink: 0;
  transition: transform .15s ease-in-out;
  will-change: transform;
}

.nav-group.open .button {
  transform: rotate(90deg);
}

.navigation>.nav-item {
  width: 250px;
}

.main-menu-content {
  overflow: hidden;
}

.main-menu-content:hover {
  overflow-y: auto;
}

.main-menu-content::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

.main-menu-content::-webkit-scrollbar-button:start:decrement,
.main-menu-content::-webkit-scrollbar-button:end:increment {
  display: none;
}

::-webkit-scrollbar-thumb:vertical {
  background-color: rgba(112, 112, 112, 0.247);
  -webkit-border-radius: 10px;
}

.main-menu.menu-dark .navigation,
.main-menu.menu-light .navigation {
  background: none;
}

.main-menu.menu-light {
  color: none;
}

/* Mobile menu visibility - ensure proper z-index and transitions */
:global(@media (max-width: 1199.98px)) {
  :global(.vertical-layout.vertical-menu-modern) .main-menu {
    z-index: 1050 !important;
  }
}

/* Overlay styling */
.sidenav-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: none;
}

.sidenav-overlay.show {
  display: block;
}

/* Remove box-shadow dos itens ativos do menu (tema light e dark) */
:global(.main-menu.menu-light .navigation > li.active > a),
:global(.main-menu.menu-dark .navigation > li.active > a),
:global(.main-menu.menu-light .navigation > li ul .active),
:global(.main-menu.menu-dark .navigation > li ul .active) {
  box-shadow: none !important;
}
</style>